import { useEffect } from "react";
import "./index.css";
import {
  FaEnvelope,
  FaExternalLinkAlt,
  FaGithub,
} from "react-icons/fa";

const contact = {
  email: "support@syscdsoftware.com",
  github: "https://github.com/SysCd",
};

const logoSrc = "/logo-wordmark.png";
const systemCodeLogoSrc = "/system-code-logo.png";

const thinkingCards = [
  {
    title: "First Principles",
    body: "Break problems down into components, assumptions, dependencies, and constraints.",
  },
  {
    title: "Systems Thinking",
    body: "Map how identity, endpoints, servers, networks, policies, automation, and operations interact.",
  },
  {
    title: "Operational Clarity",
    body: "Turn analysis into diagrams, documentation, scripts, and repeatable workflows.",
  },
];

const workingStyleCards = [
  {
    title: "Calm Under Pressure",
    body: "I stay steady during technical issues and focus on isolating symptoms, dependencies and likely failure points.",
  },
  {
    title: "Independent Operator",
    body: "I'm comfortable working through complex problems independently, using documentation, logs, testing and structured reasoning.",
  },
  {
    title: "Evidence-Driven",
    body: "I prefer practical proof over guesswork: diagrams, commands, screenshots, validation checks and repeatable fixes.",
  },
  {
    title: "Achievement-Focused",
    body: "I'm motivated by clear progress, finished outputs and technical systems that become easier to support over time.",
  },
];

const heroTags = [
  "Azure",
  "Intune",
  "Entra ID",
  "Windows Server",
  "PowerShell",
  "Microsoft 365",
  "Endpoint Management",
  "Identity & Access",
];

const softwareProducts = [
  {
    title: "Principles of Nature",
    type: "iOS App",
    description:
      "Original daily principles app with widgets, reminders, notifications, and read aloud.",
    status: "Submitted to App Store Review",
    links: [
      {
        label: "Support contact",
        href: `mailto:${contact.email}`,
      },
    ],
    note: "App Store link pending review.",
  },
  {
    title: "AI Context Bridge",
    type: "AI Tool / Private MVP",
    description:
      "Experimental SysCd tool exploring privacy-focused AI workflows and user-controlled software infrastructure.",
    status: "Private MVP",
  },
];

const projects = [
  {
    title: "Intune / Autopilot / Endpoint Compliance Lab",
    link: "https://github.com/SysCd/intune-autopilot-endpoint-lab",
    readmeLink:
      "https://github.com/SysCd/intune-autopilot-endpoint-lab/blob/main/README.md",
    tags: [
      "Microsoft Intune",
      "Entra ID",
      "Windows 11",
      "Conditional Access",
      "Defender",
    ],
    summary:
      "Built a Microsoft endpoint management lab with a Windows 11 Pro device enrolled into Entra ID and managed through Intune.",
    shortText:
      "Configured compliance policies, Settings Catalog profiles, Company Portal deployment, Defender baseline settings, and compliant-device Conditional Access testing with evidence-based documentation.",
  },
  {
    title: "Microsoft Identity & Endpoint Lab",
    link: "https://github.com/SysCd/Microsoft-Identity-Endpoint-Lab",
    tags: ["Entra ID", "Intune", "Microsoft 365", "PowerShell"],
    summary:
      "Built a dedicated Microsoft cloud administration lab covering users, groups, roles, Conditional Access, MFA, endpoint configuration, scripted exports, validation evidence, and operational documentation.",
  },
  {
    title: "Windows Server Active Directory Lab",
    link: "https://github.com/SysCd/windows-server-ad-lab",
    readmeLink: "https://github.com/SysCd/windows-server-ad-lab/blob/main/README.md",
    tags: ["Windows Server 2022", "Active Directory", "DNS", "Group Policy", "SMB"],
    summary:
      "Built a local Microsoft infrastructure lab with a Windows Server 2022 domain controller, Active Directory, DNS, Group Policy, SMB file shares, and a domain-joined client.",
    shortText:
      "Validated group-based access control end to end by creating department security groups, NTFS permissions, DNS records, domain login flows, and share access tests.",
  },
  {
    title: "Azure Infrastructure Admin Lab",
    link: "https://github.com/SysCd/Azure-Infrastructure-Admin-Lab",
    tags: ["Azure VMs", "Virtual Network", "NSGs", "Linux"],
    summary:
      "Administered Azure infrastructure with Linux virtual machines, secure remote access, virtual networking, baseline validation, user provisioning, and initial security hardening.",
  },
  {
    title: "Azure DevOps Platform Lab",
    label: "Azure Platform Lab (In Progress)",
    link: "https://github.com/SysCd/azure-devops-platform-lab",
    tags: [
      "Azure",
      "Terraform",
      "GitHub Actions",
      "Azure DevOps",
      "Containers",
      "Monitoring",
    ],
    summary:
      "Building an end-to-end Microsoft Azure DevOps platform lab covering Terraform landing zones, CI/CD, Azure App Service, containers, monitoring, observability, and platform operations.",
    shortText:
      "Planned lab modules cover reusable Terraform infrastructure, GitHub Actions deployment, Azure DevOps YAML pipelines, Azure Container Apps, Azure Monitor, Log Analytics, Application Insights, and KQL-based troubleshooting.",
    architecture:
      "Terraform landing zone -> CI/CD pipelines -> containers -> monitoring",
  },
  {
    title: "Principles of Nature iOS App Deployment",
    label: "iOS App Release",
    link: "/#principles-of-nature",
    linkLabel: "View app section",
    tags: [
      "iOS App Deployment",
      "App Store Connect",
      "TestFlight",
      "Public Release",
      "Support Email",
      "Documentation",
    ],
    summary:
      "Public release work for the Principles of Nature iOS app, covering app presentation, support contact setup, release documentation, and production-facing website readiness.",
    shortText:
      "Positioned as a lightweight deployment and operations project alongside the Microsoft infrastructure portfolio, showing release hygiene, support routing, and public-domain readiness.",
  },
  {
    title: "CortexAI / Reasoning-Enhanced SLM 2.0",
    subtitle: "Custom reasoning model + RAG research assistant",
    label: "Advanced AI Systems Lab",
    link: "https://github.com/SysCd/Reasoning-Enhanced-SLM-2.0",
    readmeLink:
      "https://github.com/SysCd/Reasoning-Enhanced-SLM-2.0/blob/main/README.md",
    diagramLink:
      "https://github.com/SysCd/Reasoning-Enhanced-SLM-2.0/tree/main/Diagrams",
    featured: true,
    tags: [
      "CortexAI",
      "Mistral-7B",
      "LoRA",
      "PEFT",
      "TRL",
      "RAG",
      "Qdrant",
      "OpenAI Embeddings",
      "Apple Silicon MPS",
      "Python",
      "JSONL Dataset",
      "500k Examples",
      "First-Principles Reasoning",
    ],
    summary:
      "Built a custom reasoning-enhanced LLM pipeline by generating a ~500k-example supervised fine-tuning dataset from first-principles reasoning blueprints, fine-tuning Mistral-7B-Instruct with LoRA, and then adding a Qdrant-backed RAG layer to turn it into CortexAI, a research-paper chat assistant.",
    shortText:
      "The project separates reasoning behaviour from factual knowledge: LoRA adapters shape the model's terse, systems-oriented reasoning style, while RAG supplies updatable context from external documents and research papers.",
    architecture:
      "Blueprint dataset -> LoRA fine-tune -> RAG retrieval -> CortexAI chat",
    details: [
      "Designed a Reasoning Blueprint System for first-principles decomposition, systems cognition, and compressed logic patterns.",
      "Generated a custom ~500k-example JSONL instruction dataset for supervised fine-tuning.",
      "Fine-tuned Mistral-7B-Instruct with LoRA adapters using PEFT and TRL on Apple Silicon MPS.",
      "Added a RAG layer using document ingestion, chunking, OpenAI text-embedding-3-small, and Qdrant vector search.",
    ],
    extraDetails: [
      "Built CortexAI as a research-paper chat interface combining the tuned reasoning style with retrieved external knowledge.",
      "Documented the architecture with diagrams covering reasoning blueprints, dataset generation, local training, and retrieval.",
    ],
  },
];

const experience = [
  {
    role: "Focused Infrastructure & Cloud Development",
    company: "Infrastructure, cloud, AI systems, and automation study",
    date: "Dec 2024 - Present",
    bullets: [
      "Built hands-on Azure, Terraform, Linux, automation, monitoring, backup, and CI/CD labs with documentation and portfolio evidence.",
      "Used AI tooling for technical research, documentation support, workflow optimisation, and AI systems experimentation.",
    ],
  },
  {
    role: "Systems Engineer",
    company: "Motorola Solutions, London",
    date: "Oct 2023 - Dec 2024",
    bullets: [
      "Improved VM provisioning speed by 40% through VMware ESXi optimisation.",
      "Administered Windows Server environments, including patching, updates, and maintenance.",
      "Supported lab infrastructure, networking, training operations, incidents, and technical issue management.",
    ],
  },
  {
    role: "Technical Support Engineer",
    company: "Utility Warehouse, London",
    date: "Oct 2022 - Mar 2023",
    bullets: [
      "Managed Active Directory, Okta, and Intune for 250+ Windows and macOS devices.",
      "Reduced downtime by 25% through proactive troubleshooting of VPN, firewall, and network issues.",
      "Documented escalated issues, fixes, and support procedures to improve knowledge sharing.",
    ],
  },
];

const skillGroups = [
  {
    title: "Professional Experience",
    skills: [
      "Windows Server",
      "Active Directory",
      "Intune",
      "Okta",
      "Entra ID",
      "Microsoft 365",
      "VMware ESXi",
      "Endpoint support",
      "Networking",
      "Troubleshooting",
      "Documentation",
    ],
  },
  {
    title: "Microsoft Infrastructure Labs",
    skills: [
      "Azure Virtual Machines",
      "Azure Virtual Network",
      "Network Security Groups",
      "Entra ID",
      "Conditional Access",
      "MFA",
      "Intune compliance/configuration policies",
      "Microsoft Graph PowerShell",
      "PowerShell automation",
      "Windows Server AD DS",
      "DNS / DHCP / GPO",
    ],
  },
  {
    title: "Supporting / Broader Labs",
    skills: [
      "Terraform",
      "Docker",
      "Kubernetes",
      "GitHub Actions",
      "Prometheus",
      "Grafana",
      "Loki",
      "Qdrant / RAG / CortexAI",
    ],
  },
];

function Header({ page }) {
  const isPortfolio = page === "portfolio";
  const navLinks = isPortfolio
    ? [
        { href: "/", label: "SysCd" },
        { href: "#projects", label: "Projects" },
        { href: "#experience", label: "Experience" },
        { href: "#skills", label: "Skills" },
        { href: "#contact", label: "Contact" },
      ]
    : [
        { href: "#software", label: "Software" },
        { href: "#about", label: "About" },
        { href: "/portfolio", label: "Portfolio" },
        { href: "#contact", label: "Contact" },
      ];

  return (
    <header className="site-header" id="top">
      <a className="brand" href="/" aria-label="SysCd Software home">
        <img src={logoSrc} alt="SysCd Software logo" />
      </a>
      <nav className="nav-links" aria-label="Main navigation">
        {navLinks.map((link) => (
          <a href={link.href} key={link.label}>
            {link.label}
          </a>
        ))}
      </nav>
    </header>
  );
}

function Footer({ onBackToTop }) {
  return (
    <footer className="site-footer">
      <div className="footer-brand">
        <img className="footer-logo" src={logoSrc} alt="SysCd Software logo" />
        <p className="footer-name">SysCd Software™</p>
        <p>
          SysCd &mdash; System Code. Practical software and infrastructure,
          built from first principles.
        </p>
        <p>© 2026 Erik Gombar. All rights reserved.</p>
      </div>
      <div className="footer-links">
        <a href="/portfolio">Portfolio</a>
        <a href="#top" onClick={onBackToTop}>
          Back to top
        </a>
      </div>
    </footer>
  );
}

function SoftwareSection() {
  return (
    <section className="section-block software-section" id="software">
      <div className="section-heading">
        <p className="eyebrow">Software</p>
        <h2>Software by SysCd</h2>
        <p>
          Independent apps and tools built around systems, infrastructure, and
          AI.
        </p>
      </div>
      <div className="software-grid">
        {softwareProducts.map((product) => (
          <article
            className="software-card"
            id={
              product.title === "Principles of Nature"
                ? "principles-of-nature"
                : undefined
            }
            key={product.title}
          >
            <div>
              <span className="project-label">{product.type}</span>
              <h3>{product.title}</h3>
              <p>{product.description}</p>
            </div>
            <div className="software-meta">
              <span className="status-pill">{product.status}</span>
              {product.note && <span>{product.note}</span>}
            </div>
            {product.links && (
              <div className="project-actions">
                {product.links.map((link) => (
                  <a className="text-link" href={link.href} key={link.label}>
                    {link.label}
                    <FaExternalLinkAlt aria-hidden="true" />
                  </a>
                ))}
              </div>
            )}
          </article>
        ))}
      </div>
    </section>
  );
}

function HomePage() {
  return (
    <main>
      <section className="hero-section home-hero" aria-labelledby="home-title">
        <div className="hero-copy">
          <p className="eyebrow">SYSTEM CODE</p>
          <h1 className="brand-hero-title" id="home-title">
            SysCd
          </h1>
          <p className="hero-lede">
            Practical software and infrastructure, built from first principles.
          </p>
          <p className="home-summary">
            Independent software, infrastructure tools, and AI-focused products
            by Erik Gombar.
          </p>
          <div className="hero-actions" aria-label="Homepage actions">
            <a className="button primary" href="#software">
              View Software
            </a>
            <a className="button secondary" href="/portfolio">
              View Portfolio
            </a>
          </div>
        </div>
      </section>

      <SoftwareSection />

      <section className="section-block" id="products">
        <div className="section-heading">
          <p className="eyebrow">Products</p>
          <h2>Product Focus</h2>
          <p>
            SysCd products are small, practical systems: shipped or in-progress
            tools with clear support paths, careful documentation, and a focus
            on useful software rather than noise.
          </p>
        </div>
        <div className="specialty-grid">
          <article className="specialty-card">
            <h3>Independent Apps</h3>
            <p>
              Focused iOS products built around daily use, reading, reminders,
              widgets, and clean support operations.
            </p>
          </article>
          <article className="specialty-card">
            <h3>Infrastructure Tools</h3>
            <p>
              Practical tooling ideas shaped by Microsoft infrastructure,
              automation, documentation, and operational reliability.
            </p>
          </article>
          <article className="specialty-card">
            <h3>AI-Focused Systems</h3>
            <p>
              Private experiments around privacy-focused AI workflows and
              user-controlled software infrastructure.
            </p>
          </article>
        </div>
      </section>

      <section className="section-block split-section" id="about">
        <div className="section-heading sticky-heading">
          <p className="eyebrow">About SysCd</p>
          <h2>Software from systems thinking.</h2>
        </div>
        <div className="timeline-list">
          <article className="timeline-entry">
            <img
              className="system-code-mark system-code-mark-small"
              src={systemCodeLogoSrc}
              alt="System Code logo"
            />
            <p>
              SysCd Software™ is Erik Gombar&apos;s personal software brand for
              independent apps, tools, and infrastructure-focused projects.
            </p>
            <p>
              The work sits at the intersection of practical software,
              Microsoft infrastructure, automation, AI-assisted workflows, and
              first-principles documentation.
            </p>
          </article>
        </div>
      </section>

      <section className="contact-section" id="contact">
        <div>
          <p className="eyebrow">Contact</p>
          <h2>Software, support, and technical enquiries.</h2>
          <p>
            For product support, collaborations, or technical enquiries, contact
            me at support@syscdsoftware.com.
          </p>
        </div>
        <div className="contact-actions">
          <a className="button primary" href={`mailto:${contact.email}`}>
            <FaEnvelope aria-hidden="true" />
            {contact.email}
          </a>
          <a
            className="button secondary"
            href={contact.github}
            target="_blank"
            rel="noopener noreferrer"
          >
            <FaGithub aria-hidden="true" />
            GitHub
          </a>
        </div>
      </section>
    </main>
  );
}

function PortfolioPage() {
  return (
    <main>
      <section className="hero-section" aria-labelledby="hero-title">
        <div className="hero-copy">
          <p className="eyebrow">Azure &amp; Microsoft Infrastructure Engineer</p>
          <h1 id="hero-title">Erik Gombar</h1>
          <p className="hero-lede">
            Building reliable systems across identity, endpoints, automation,
            cloud infrastructure, and deployment workflows.
          </p>
          <div className="hero-tags" aria-label="Microsoft infrastructure focus">
            {heroTags.map((tag) => (
              <span key={tag}>{tag}</span>
            ))}
          </div>
          <div className="hero-actions" aria-label="Primary contact links">
            <a className="button primary" href={`mailto:${contact.email}`}>
              <FaEnvelope aria-hidden="true" />
              Email
            </a>
            <a
              className="button secondary"
              href={contact.github}
              target="_blank"
              rel="noopener noreferrer"
            >
              <FaGithub aria-hidden="true" />
              GitHub
            </a>
          </div>
        </div>
      </section>

      <section className="section-block thinking-section" id="thinking">
        <div className="section-heading">
          <p className="eyebrow">Approach</p>
          <h2>First-Principles Approach</h2>
          <p>
            I approach infrastructure from first principles: what components
            exist, how they connect, what assumptions they depend on, and where
            they can fail.
          </p>
          <p>
            My systems-thinking approach helps me map identity, endpoints,
            servers, networking, policies, automation, monitoring, access paths,
            and failure points into clear diagrams, fixes, and repeatable
            workflows.
          </p>
        </div>
        <div className="thinking-grid">
          {thinkingCards.map((card) => (
            <article className="thinking-card" key={card.title}>
              <h3>{card.title}</h3>
              <p>{card.body}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="section-block working-section" id="working-style">
        <div className="section-heading">
          <p className="eyebrow">Working Style</p>
          <h2>Working Style</h2>
          <p>
            I&apos;m a calm, independent and systems-focused engineer who works
            best with clear problems, practical evidence and well-documented
            technical environments.
          </p>
          <p>
            I work best in technical environments where identity, endpoint,
            server, network, and cloud components need to be understood clearly,
            documented properly, and improved systematically.
          </p>
          <p>
            I prefer to understand the underlying structure of a system before
            changing it: the components, dependencies, assumptions, access paths
            and likely failure points.
          </p>
          <p>
            My working style is direct, analytical and outcome-focused: map the
            system, isolate the issue, test the fix, document the result, and
            make the process easier to repeat.
          </p>
        </div>
        <div className="working-grid">
          {workingStyleCards.map((card) => (
            <article className="working-card" key={card.title}>
              <h3>{card.title}</h3>
              <p>{card.body}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="section-block" id="projects">
        <div className="section-heading">
          <p className="eyebrow">Selected labs</p>
          <h2>Technical Projects</h2>
          <p>
            Microsoft infrastructure is the core focus: identity, endpoints,
            Windows Server, Azure, automation, monitoring, and operational
            documentation. Additional cloud and AI systems projects demonstrate
            broader systems-thinking, automation, and architecture skills.
          </p>
        </div>
        <div className="project-grid">
          {projects.map((project) => (
            <article
              className={`project-card ${
                project.featured ? "featured-project" : ""
              }`}
              id={
                project.title === "Principles of Nature iOS App Deployment"
                  ? "principles-of-nature"
                  : undefined
              }
              key={project.title}
            >
              <div>
                {project.label && (
                  <span className="project-label">{project.label}</span>
                )}
                <h3>{project.title}</h3>
                {project.subtitle && (
                  <p className="project-subtitle">{project.subtitle}</p>
                )}
                <p>{project.summary}</p>
                {project.shortText && <p>{project.shortText}</p>}
                {project.architecture && (
                  <p className="architecture-line">{project.architecture}</p>
                )}
                {project.details && (
                  <ul className="project-details">
                    {project.details.map((detail) => (
                      <li key={detail}>{detail}</li>
                    ))}
                  </ul>
                )}
                {project.extraDetails && (
                  <details className="project-more">
                    <summary>More architecture notes</summary>
                    <ul>
                      {project.extraDetails.map((detail) => (
                        <li key={detail}>{detail}</li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
              <div className="tag-row">
                {project.tags.map((tag) => (
                  <span key={tag}>{tag}</span>
                ))}
              </div>
              <div className="project-actions">
                <a
                  className="text-link"
                  href={project.link}
                  target={project.link.startsWith("http") ? "_blank" : undefined}
                  rel={
                    project.link.startsWith("http")
                      ? "noopener noreferrer"
                      : undefined
                  }
                >
                  {project.linkLabel || "View repository"}
                  <FaExternalLinkAlt aria-hidden="true" />
                </a>
                {project.demoLink && (
                  <a
                    className="text-link"
                    href={project.demoLink}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View CortexAI demo
                    <FaExternalLinkAlt aria-hidden="true" />
                  </a>
                )}
                {project.writeupLink && (
                  <a
                    className="text-link"
                    href={project.writeupLink}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Read write-up
                    <FaExternalLinkAlt aria-hidden="true" />
                  </a>
                )}
                {project.readmeLink && (
                  <a
                    className="text-link"
                    href={project.readmeLink}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Read README
                    <FaExternalLinkAlt aria-hidden="true" />
                  </a>
                )}
                {project.diagramLink && (
                  <a
                    className="text-link"
                    href={project.diagramLink}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    View diagrams
                    <FaExternalLinkAlt aria-hidden="true" />
                  </a>
                )}
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="section-block split-section" id="experience">
        <div className="section-heading sticky-heading">
          <p className="eyebrow">Professional record</p>
          <h2>Experience</h2>
          <p>
            Microsoft-focused infrastructure and support experience across
            Windows Server, Active Directory, Intune, Okta, Microsoft 365,
            VMware ESXi, endpoint support, networking, documentation, and
            troubleshooting.
          </p>
        </div>
        <div className="timeline-list">
          {experience.map((item) => (
            <article className="timeline-entry" key={item.role}>
              <span className="date-pill">{item.date}</span>
              <h3>{item.role}</h3>
              <p className="company">{item.company}</p>
              <ul>
                {item.bullets.map((bullet) => (
                  <li key={bullet}>{bullet}</li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>

      <section className="section-block" id="skills">
        <div className="section-heading">
          <p className="eyebrow">Tooling range</p>
          <h2>Technical Skills</h2>
          <p>
            Split by where the evidence comes from: professional Microsoft
            infrastructure experience, Microsoft-focused labs, and supporting
            broader cloud and systems projects.
          </p>
        </div>
        <div className="skill-groups">
          {skillGroups.map((group) => (
            <article className="skill-group" key={group.title}>
              <h3>{group.title}</h3>
              <div className="tag-row">
                {group.skills.map((skill) => (
                  <span key={skill}>{skill}</span>
                ))}
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="section-block education-section">
        <div className="section-heading education-heading">
          <p className="eyebrow">Education</p>
        </div>
        <article className="education-card">
          <h2>BSc (Hons) Computer Science</h2>
          <p>Brunel University London · 2018–2022</p>
          <p>Academic Excellence CEDPS Scholarship Recipient</p>
        </article>
      </section>

      <section className="contact-section" id="contact">
        <div>
          <p className="eyebrow">Get in touch</p>
          <h2>Available for infrastructure and cloud engineering roles.</h2>
          <p>
            For roles, collaborations, or technical enquiries, contact me at
            support@syscdsoftware.com.
          </p>
        </div>
        <div className="contact-actions">
          <a className="button primary" href={`mailto:${contact.email}`}>
            <FaEnvelope aria-hidden="true" />
            {contact.email}
          </a>
          <a
            className="button secondary"
            href={contact.github}
            target="_blank"
            rel="noopener noreferrer"
          >
            <FaGithub aria-hidden="true" />
            GitHub
          </a>
        </div>
      </section>
    </main>
  );
}

function App() {
  const normalizedPath = window.location.pathname.replace(/\/$/, "") || "/";
  const page = normalizedPath === "/portfolio" ? "portfolio" : "home";

  useEffect(() => {
    document.title =
      page === "portfolio"
        ? "Erik Gombar | Azure & Microsoft Infrastructure Engineer"
        : "SysCd Software";
  }, [page]);

  useEffect(() => {
    const sections = document.querySelectorAll(
      ".hero-section, .section-block, .contact-section, .site-footer"
    );
    const prefersReducedMotion =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    sections.forEach((section) => {
      section.classList.add("reveal-section");
      if (section.getBoundingClientRect().top < window.innerHeight * 0.9) {
        section.classList.add("is-visible");
      }
    });

    if (prefersReducedMotion || typeof IntersectionObserver === "undefined") {
      sections.forEach((section) => {
        section.classList.add("is-visible");
      });
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      {
        rootMargin: "0px 0px -80px 0px",
        threshold: 0.12,
      }
    );

    sections.forEach((section) => {
      observer.observe(section);
    });

    return () => {
      observer.disconnect();
    };
  }, [page]);

  const handleBackToTop = (event) => {
    event.preventDefault();
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  return (
    <div className="site-shell">
      <Header page={page} />
      {page === "portfolio" ? <PortfolioPage /> : <HomePage />}
      <Footer onBackToTop={handleBackToTop} />
    </div>
  );
}

export default App;
